import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import { isCanceledError } from 'src/helper/api';
import {
  applyIntelligentRoutingSuggestions,
  getApplyStatus,
  getIntelligentRoutingStatus,
  getIntelligentSchedulingData,
  runIntelligentSchedulingOptimizer,
  setWorkOrderLock,
} from 'src/redux/scheduling/api';
import type { RawSchedulingResponse } from 'src/redux/scheduling/normalizer';
import {
  normalizeSchedulingResponse,
  setOptimizerSuggestions,
} from 'src/redux/scheduling/normalizer';
import type { ApplyStatusResponse, SchedulingState } from 'src/redux/scheduling/types';

const extraReducers = (builder: ActionReducerMapBuilder<SchedulingState>) => {
  // Load initial scheduling data
  builder.addCase(getIntelligentSchedulingData().pending, (state) => {
    state.isLoadingData = true;
    state.hasLoadError = false;
  });

  builder.addCase(getIntelligentSchedulingData().fulfilled, (state, action) => {
    const payload = action.payload as RawSchedulingResponse;
    // The market zone the backend always sends (InitialSchedulingDataPresenter.php:72).
    // No `??` fallback: substituting one here would render every instant on the board in
    // a zone the payload never claimed. `normalized.timezone` already carries it.
    const { timezone } = payload;
    const normalized = normalizeSchedulingResponse(payload, timezone);
    Object.assign(state, { ...normalized, isLoadingData: false, hasLoadError: false });
  });

  builder.addCase(getIntelligentSchedulingData().rejected, (state, action) => {
    // Check BOTH. The thunk factory marks an abort with
    // `rejectWithValue({ actionType: 'canceled' })` (src/helper/api.ts:156), and RTK routes
    // a rejectWithValue result to `action.payload`, leaving `action.error` as its generic
    // `{ message: 'Rejected' }`. Reading `action.error` alone never matched, so every
    // aborted load — a market switch, a date change, an unmount — was surfaced as a load
    // failure. `action.error` still matters for a raw AbortError that never reached the
    // factory. Mirrors vendor-preferences/extra-reducer.ts, activity-log, lumi-conversation
    // and external-connections, which all check the payload.
    if (isCanceledError(action.payload) || isCanceledError(action.error)) return;
    state.isLoadingData = false;
    // ORI-415: mark the load as errored so the timeline shows an error state
    // rather than the bare empty grid (a failed load ≠ an empty day).
    state.hasLoadError = true;
  });

  // Run optimizer
  builder.addCase(runIntelligentSchedulingOptimizer().pending, (state) => {
    state.isOptimizerRunning = true;
    state.prevRoutingRequestId = state.routingRequestId;
    state.routingRequestId = null;
    state.applyBatchId = null;
    state.applyResult = null;
  });

  builder.addCase(runIntelligentSchedulingOptimizer().fulfilled, (state, action) => {
    if (action.payload.status === 'failed') {
      state.isOptimizerRunning = false;
      state.routingRequestId = state.prevRoutingRequestId;
      state.prevRoutingRequestId = null;
      return;
    }
    state.routingRequestId = action.payload.routing_request_uuid;
    state.prevRoutingRequestId = null;
  });

  builder.addCase(runIntelligentSchedulingOptimizer().rejected, (state) => {
    state.isOptimizerRunning = false;
    state.routingRequestId = state.prevRoutingRequestId;
    state.prevRoutingRequestId = null;
  });

  // Get routing status
  builder.addCase(getIntelligentRoutingStatus().fulfilled, (state, action) => {
    if (action?.payload?.status !== 'processing') {
      state.isOptimizerRunning = false;
    }
    if (action.payload.status === 'succeeded') {
      state.optimizerHasRun = true;
      const { daily_schedules, assignments, unassigned, summary } = action.payload.provider_payload;
      setOptimizerSuggestions(state, daily_schedules, assignments, unassigned, state.timezone);
      state.routingRequestId = action.payload.routing_request_uuid ?? state.routingRequestId;
      state.optimizerSummary = summary;
      state.suggestedWOCount = state.technicians.allIds.reduce(
        (acc, id) => acc + (state.technicians.byId[id]?.suggestedWOIds?.length ?? 0),
        0,
      );
      const scheduledPart = state.suggestedWOCount ? `${state.suggestedWOCount} Suggestions` : '';
      const unscheduledPart = summary?.unscheduled_work_orders
        ? `${summary.unscheduled_work_orders} Unable`
        : '';
      state.schedulingAlert = {
        message: [scheduledPart, unscheduledPart].filter(Boolean).join(', '),
      };
    }
  });

  builder.addCase(getIntelligentRoutingStatus().rejected, (state) => {
    state.isOptimizerRunning = false;
  });

  // Lock / unlock work order (optimistic update)
  // Dispatch as: dispatch(setWorkOrderLock(uuid, lock, token, signal)({ work_order_uuid: uuid, is_locked: lock }))
  builder.addCase(setWorkOrderLock().pending, (state, action) => {
    const { work_order_uuid, is_locked } = action.meta.arg as {
      work_order_uuid: string;
      is_locked: boolean;
    };
    const wo = state.workOrders.byId[work_order_uuid];
    if (wo) {
      state.pendingLockRollbacks[work_order_uuid] = wo.locked;
      wo.locked = is_locked;
      // NEVER optimistically write 'manual'. The lock endpoint no-ops when the work
      // order is already locked and hardcodes is_locked:true in its response, and no
      // lock broadcast exists — so a board left open across the nightly auto-lock sweep
      // would render a false origin for the whole round trip. Null shows the
      // origin-free copy for ~200ms and `fulfilled` then installs the server truth.
      wo.lockType = null;
    }
  });

  builder.addCase(setWorkOrderLock().fulfilled, (state, action) => {
    // `is_locked` here is the CLIENT's requested value — the api helper merges the
    // pass-through payload last — but `lock_type` survives untouched from the server
    // response. Read it; never derive `is_locked ? 'manual' : null`, because the server
    // reports the pre-existing automatic origin when the work order was already locked.
    const { work_order_uuid, is_locked, lock_type } = action.payload as {
      work_order_uuid: string;
      is_locked: boolean;
      lock_type?: string | null;
    };
    const wo = state.workOrders.byId[work_order_uuid];
    if (wo) {
      wo.locked = is_locked;
      // Gated on is_locked, mirroring this reducer's other three lock write sites so all four
      // agree. BE #1808 resolves both fields from one read, so a type alongside
      // is_locked: false should be unreachable; the guard makes it unrepresentable *here*.
      //
      // Scoped to this file deliberately: the normalizer's own lockType write is UNGATED by
      // design (see normalizer.ts — the single isSuggested/isLocked render guard lives in
      // ScheduledWOBlock, and a second guard there would drift). These reducers apply
      // per-payload deltas where both fields arrive together; the normalizer builds an entry
      // whose render guard is downstream. Same field, two justified postures.
      wo.lockType = is_locked ? (lock_type ?? null) : null;
    }
    delete state.pendingLockRollbacks[work_order_uuid];
  });

  builder.addCase(setWorkOrderLock().rejected, (state, action) => {
    const { work_order_uuid } = (action.payload ?? action.meta.arg) as { work_order_uuid: string };
    const wo = state.workOrders.byId[work_order_uuid];
    if (wo && work_order_uuid in state.pendingLockRollbacks) {
      wo.locked = state.pendingLockRollbacks[work_order_uuid];
      // Agreed degrade: the rollback map stores only the boolean, so a failed request
      // loses the origin sentence and falls back to the origin-free locked copy. Less
      // information, never a false claim.
      wo.lockType = null;
    }
    delete state.pendingLockRollbacks[work_order_uuid];
  });

  // Apply routing suggestions
  builder.addCase(applyIntelligentRoutingSuggestions().pending, (state) => {
    state.isApplyingSuggestions = true;
    state.applyBatchId = null;
  });

  builder.addCase(applyIntelligentRoutingSuggestions().fulfilled, (state, action) => {
    state.routingRequestId = action.payload.routing_request_uuid;
    state.applyBatchId = action.payload.apply_batch_id;
    const { appointments } = action.meta.arg as { appointments: { work_order_uuid: string }[] };
    for (const { work_order_uuid } of appointments) {
      const wo = state.workOrders.byId[work_order_uuid];
      if (wo) wo.applyStatus = 'processing';
    }
  });

  builder.addCase(applyIntelligentRoutingSuggestions().rejected, (state) => {
    state.isApplyingSuggestions = false;
  });

  builder.addCase(getApplyStatus().fulfilled, (state, action) => {
    const payload = action.payload as ApplyStatusResponse;

    if (!payload.is_active) {
      state.isApplyingSuggestions = false;
      state.optimizerHasRun = false;
      state.lockedSuggestions = [];
      state.applyResult = {
        completed: !payload.is_active,
        total: payload.total,
        succeeded: payload.succeeded,
        skipped: payload.skipped,
        failed: payload.failed,
        assignedWOIds: payload.items
          .filter((item) => item.status === 'succeeded' || item.status === 'skipped')
          .map((item) => item.work_order_uuid),
      };
      state.schedulingAlert = {
        message:
          payload.succeeded > 0 || payload.skipped > 0
            ? 'Schedule applied successfully.'
            : 'Schedule apply failed.',
      };
    }

    state.routingRequestId = payload.routing_request_uuid;
    state.applyBatchId = payload.apply_batch_id;

    for (const item of payload.items) {
      if (item.status === 'processing' || item.status === 'queued') continue;

      const wo = state.workOrders.byId[item.work_order_uuid];
      if (!wo) continue;

      const tech = state.technicians.byId[item.technician_uuid];
      if (tech) {
        tech.suggestedWOIds = tech.suggestedWOIds.filter((id) => id !== item.work_order_uuid);
      }

      if (wo.applyStatus === item.status) continue;

      if (item.status === 'succeeded') {
        wo.status = 'assigned';
        wo.applyStatus = 'succeeded';
        wo.isReschedule = false;
        wo.preOptimizerSchedule = undefined;
        if (typeof item.is_locked === 'boolean') {
          wo.locked = item.is_locked;
          // The apply path is the only way the automatic lock origins reach the board
          // without a full reload — all three auto-lock rules fire during apply.
          wo.lockType = item.is_locked ? (item.lock_type ?? null) : null;
        }
        if (tech && !tech.workOrderIds.includes(item.work_order_uuid)) {
          tech.workOrderIds.push(item.work_order_uuid);
        }
      } else if (item.status === 'skipped') {
        // Skipped means the suggestion already matches the current schedule — WO is assigned.
        // Treat like succeeded: mark assigned and add to the technician's work order list
        // (guarded to avoid duplicates in re-optimization scenarios where it's already there).
        wo.status = 'assigned';
        wo.applyStatus = 'skipped';
        wo.isReschedule = false;
        wo.preOptimizerSchedule = undefined;
        if (tech && !tech.workOrderIds.includes(item.work_order_uuid)) {
          tech.workOrderIds.push(item.work_order_uuid);
        }
      } else if (item.status === 'failed') {
        wo.status = 'unassigned';
        wo.applyStatus = 'failed';
        wo.unscheduledReasonDetail = item.reason_detail ?? undefined;
        state.workOrders.unscheduledWOIds?.push(item.work_order_uuid);
      }
    }
  });

  builder.addCase(getApplyStatus().rejected, (state) => {
    state.isApplyingSuggestions = false;
  });
};

export default extraReducers;
